import React, { useState, useEffect, useCallback, memo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Info } from "lucide-react";

// Normaliza un precio numérico a string editable ("" si no aplica).
const initPriceString = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return String(value);
};

/**
 * ServiceForm — Formulario de alta/edición de un servicio del catálogo.
 *
 * Mantiene estado local en string para los campos de precio (`precio_base`,
 * `precio_paquete`, `penalizacion_cancelacion`) para permitir escribir decimales
 * ("8.09"); parsea a número en `onBlur` y propaga vía `onFieldChange`.
 *
 * @param {Object}   props.service       Servicio en edición (nombre, categoría, proveedor, precios, permanencia, IVA/paquete, etc.).
 * @param {Function} props.onFieldChange (field, value) => void — actualiza un campo del servicio en el padre.
 * @param {Array}    props.providers     Proveedores disponibles para el selector.
 * @param {Array}    props.categories    Categorías disponibles para el selector.
 * @param {Function} props.onSubmit      Handler de guardado (crear/actualizar).
 * @param {string}   props.submitText    Texto del botón de confirmación.
 * @param {Function} props.onCancel      Cierra el diálogo sin guardar.
 */
const ServiceForm = memo(({ service, onFieldChange, providers, categories, onSubmit, submitText, onCancel }) => {
  // Local string state for price fields to support typing decimals like "8.09"
  const [priceStrings, setPriceStrings] = useState(() => ({
    precio_base: initPriceString(service.precio_base),
    precio_paquete: initPriceString(service.precio_paquete),
    penalizacion_cancelacion: initPriceString(service.penalizacion_cancelacion),
  }));

  useEffect(() => {
    setPriceStrings({
      precio_base: initPriceString(service.precio_base),
      precio_paquete: initPriceString(service.precio_paquete),
      penalizacion_cancelacion: initPriceString(service.penalizacion_cancelacion),
    });
  }, [service.precio_base, service.precio_paquete, service.penalizacion_cancelacion]);

  const handleChange = useCallback((field, value) => {
    onFieldChange(field, value);
  }, [onFieldChange]);

  // Only update local string state; allow digits, one dot/comma, optional leading minus
  const handlePriceInput = useCallback((field, rawValue) => {
    let value = rawValue.replace(",", ".");
    if (value !== "" && !/^-?\d*\.?\d*$/.test(value)) return;
    setPriceStrings(prev => ({ ...prev, [field]: value }));
  }, []);

  // Parse to number on blur and propagate to parent
  const handlePriceBlur = useCallback((field) => {
    const raw = priceStrings[field];
    if (raw === "" || raw === "-" || raw === ".") {
      onFieldChange(field, null);
      return;
    }
    const num = parseFloat(raw);
    onFieldChange(field, isNaN(num) ? null : num);
  }, [priceStrings, onFieldChange]);

  return (
    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
      <div>
        <Label className="text-slate-300">Nombre *</Label>
        <Input
          value={service.nombre}
          onChange={(e) => handleChange("nombre", e.target.value)}
          placeholder="Ej: Fibra 1Gbps Simétrica"
          data-testid="service-name-input"
          className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Categoría *</Label>
          <Select
            value={service.categoria}
            onValueChange={(v) => handleChange("categoria", v)}
          >
            <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
              <SelectValue placeholder="Selecciona categoría" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
              {categories.map((cat) => (
                <SelectItem key={cat.id || cat.name} value={cat.name} className="text-white">
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-300">Proveedor</Label>
          <Select
            value={service.proveedor_id || ""}
            onValueChange={(v) => handleChange("proveedor_id", v === "none" ? "" : v)}
          >
            <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
              <SelectValue placeholder="Selecciona proveedor" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
              <SelectItem value="none" className="text-slate-400">
                Sin proveedor
              </SelectItem>
              {providers.map((prov) => (
                <SelectItem key={prov.id} value={prov.id} className="text-white">
                  {prov.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-slate-300">Descripción</Label>
        <Textarea
          value={service.descripcion || ""}
          onChange={(e) => handleChange("descripcion", e.target.value)}
          placeholder="Descripción del servicio..."
          rows={2}
          className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-slate-300">Precio Standalone (€ con IVA)</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={priceStrings.precio_base}
            onChange={(e) => handlePriceInput("precio_base", e.target.value)}
            onBlur={() => handlePriceBlur("precio_base")}
            placeholder="Vacío si no aplica"
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Permanencia (meses)</Label>
          <Input
            type="number"
            value={service.permanencia_meses || ""}
            onChange={(e) => handleChange("permanencia_meses", parseInt(e.target.value) || 0)}
            min="0"
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Penalización (€)</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={priceStrings.penalizacion_cancelacion}
            onChange={(e) => handlePriceInput("penalizacion_cancelacion", e.target.value)}
            onBlur={() => handlePriceBlur("penalizacion_cancelacion")}
            placeholder="0"
            className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
          />
        </div>
      </div>

      {/* New Section: Package Configuration */}
      <div className="border-t border-[#1E1E5F] pt-4 mt-4">
        <h3 className="text-sm font-semibold text-[#03BEE4] mb-3">Configuración de Precios y Paquetes</h3>
        
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Precio en Paquete (€ con IVA)</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={priceStrings.precio_paquete}
              onChange={(e) => handlePriceInput("precio_paquete", e.target.value)}
              onBlur={() => handlePriceBlur("precio_paquete")}
              placeholder="Dejar vacío si no aplica en paquetes"
              className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
            />
            <p className="text-xs text-slate-500 mt-1">El precio sin IVA se calcula automáticamente (÷ 1,21). Acepta valores negativos para descuentos.</p>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Label className="text-slate-300">Líneas que genera al seleccionarse</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-slate-500" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-xs">
                    <p>Normalmente es 1. Si el producto incluye varias líneas (ej: "(2x) Móvil"), indica aquí cuántas líneas crea automáticamente en el presupuesto.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              type="number"
              value={service.unidades_por_seleccion || 1}
              onChange={(e) => handleChange("unidades_por_seleccion", Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white w-24"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-slate-300">Activo</Label>
        <Switch
          checked={service.is_active}
          onCheckedChange={(v) => handleChange("is_active", v)}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button
          variant="outline"
          onClick={onCancel}
          className="border-[#1E1E5F] text-slate-300"
        >
          Cancelar
        </Button>
        <Button
          onClick={onSubmit}
          data-testid="submit-service-btn"
          className="bg-[#42E695] text-[#04042C]"
        >
          {submitText}
        </Button>
      </div>
    </div>
  );
});

ServiceForm.displayName = "ServiceForm";

/**
 * ServiceModal — Diálogos de "Nuevo Servicio" y "Editar Servicio" del catálogo.
 *
 * Extraído 1:1 desde `pages/CatalogPage.js`. Ambos diálogos comparten `ServiceForm`;
 * el de creación usa `newService`/`handleNewServiceFieldChange`/`handleCreateService`
 * y el de edición `editingService`/`handleEditingServiceFieldChange`/`handleUpdateService`.
 *
 * @param {boolean}     props.createOpen                       Apertura del diálogo de creación.
 * @param {Function}    props.setCreateOpen                     Setter del flag de creación.
 * @param {Object}      props.newService                        Estado del servicio nuevo.
 * @param {Function}    props.handleNewServiceFieldChange       (field, value) => void para el servicio nuevo.
 * @param {Function}    props.handleCreateService               Handler de creación de servicio.
 * @param {Object|null} props.editingService                    Servicio en edición (null = cerrado).
 * @param {Function}    props.setEditingService                 Setter del servicio en edición.
 * @param {Function}    props.handleEditingServiceFieldChange   (field, value) => void para el servicio en edición.
 * @param {Function}    props.handleUpdateService               Handler de actualización de servicio.
 * @param {Array}       props.providers                         Proveedores disponibles.
 * @param {Array}       props.categories                        Categorías disponibles.
 */
export const ServiceModal = ({
  createOpen,
  setCreateOpen,
  newService,
  handleNewServiceFieldChange,
  handleCreateService,
  editingService,
  setEditingService,
  handleEditingServiceFieldChange,
  handleUpdateService,
  providers,
  categories,
}) => {
  return (
    <>
      {/* Create Service Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Nuevo Servicio</DialogTitle>
          </DialogHeader>
          <ServiceForm
            service={newService}
            onFieldChange={handleNewServiceFieldChange}
            providers={providers}
            categories={categories}
            onSubmit={handleCreateService}
            submitText="Crear Servicio"
            onCancel={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Service Modal */}
      <Dialog open={!!editingService} onOpenChange={(open) => !open && setEditingService(null)}>
        <DialogContent className="bg-[#0F0F3D] border-[#1E1E5F] text-white max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Editar Servicio</DialogTitle>
          </DialogHeader>
          {editingService && (
            <ServiceForm
              service={editingService}
              onFieldChange={handleEditingServiceFieldChange}
              providers={providers}
              categories={categories}
              onSubmit={handleUpdateService}
              submitText="Guardar Cambios"
              onCancel={() => setEditingService(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
